// =============================================================================
// PublicDayPicker — calendario popover per il flow pubblico
// =============================================================================
// Calendario mensile self-contained (indipendente dal MiniCalendar admin):
//   - vincoli min/max: disabilita i giorni fuori dalla finestra di prenotazione
//     del tenant (minAdvanceHours / maxAdvanceDays)
//   - colore del giorno selezionato dal primary del tenant
//     (var(--skedulab-public-primary))
// =============================================================================

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  startOfDay,
  isBefore,
  isAfter,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDateLocale } from '../../hooks/useDateLocale';

interface Props {
  /** Giorno attualmente selezionato (start of day) */
  value: Date;
  /** Primo giorno selezionabile (start of day) */
  min: Date;
  /** Ultimo giorno selezionabile (start of day) */
  max: Date;
  onSelect: (day: Date) => void;
  onClose: () => void;
}

export function PublicDayPicker({ value, min, max, onSelect, onClose }: Props) {
  const dateLocale = useDateLocale();
  const ref = useRef<HTMLDivElement>(null);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(value));

  // Chiudi con click fuori
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Chiudi con Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const minMonth = useMemo(() => startOfMonth(min), [min]);
  const maxMonth = useMemo(() => startOfMonth(max), [max]);
  const canPrev = viewMonth > minMonth;
  const canNext = viewMonth < maxMonth;

  const handleSelect = useCallback(
    (day: Date) => {
      onSelect(startOfDay(day));
      onClose();
    },
    [onSelect, onClose],
  );

  // Griglia giorni (settimana da lunedì)
  const weeks = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    const rows: Date[][] = [];
    let day = gridStart;
    while (day <= gridEnd) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(day);
        day = addDays(day, 1);
      }
      rows.push(week);
    }
    return rows;
  }, [viewMonth]);

  const dayNames = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i), 'EEEEE', {
          locale: dateLocale,
        }),
      ),
    [dateLocale],
  );

  return (
    <div
      ref={ref}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-3 w-[280px] select-none"
    >
      {/* Header mese */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => canPrev && setViewMonth(subMonths(viewMonth, 1))}
          disabled={!canPrev}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label="Mese precedente"
        >
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>
        <span className="text-sm font-semibold text-gray-900 capitalize">
          {format(viewMonth, 'MMMM yyyy', { locale: dateLocale })}
        </span>
        <button
          type="button"
          onClick={() => canNext && setViewMonth(addMonths(viewMonth, 1))}
          disabled={!canNext}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label="Mese successivo"
        >
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Griglia */}
      <div className="grid grid-cols-7 gap-0.5">
        {dayNames.map((name, i) => (
          <div
            key={i}
            className="h-8 flex items-center justify-center text-[11px] font-medium text-gray-400 uppercase"
          >
            {name}
          </div>
        ))}

        {weeks.flatMap((week) =>
          week.map((d) => {
            const inMonth = isSameMonth(d, viewMonth);
            const selected = isSameDay(d, value);
            const disabled = isBefore(d, min) || isAfter(d, max);
            const today_ = isToday(d);

            return (
              <button
                type="button"
                key={d.toISOString()}
                onClick={() => !disabled && handleSelect(d)}
                disabled={disabled}
                className={[
                  'h-8 w-8 mx-auto rounded-lg text-sm transition-all duration-100',
                  disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'hover:bg-gray-100 cursor-pointer',
                  !disabled && !inMonth && !selected ? 'text-gray-400' : '',
                  !disabled && inMonth && !selected ? 'text-gray-700' : '',
                  today_ && !selected && !disabled ? 'font-bold ring-1 ring-gray-200' : '',
                  selected ? 'text-white font-semibold shadow-sm' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={
                  selected
                    ? { background: 'var(--skedulab-public-primary, #10B981)' }
                    : today_ && !disabled
                    ? { color: 'var(--skedulab-public-primary, #10B981)' }
                    : undefined
                }
              >
                {format(d, 'd')}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}
